// src/pages/HomeScreen.js

import React, { useState, useEffect } from 'react';
import Slider from 'react-slick'; // Import react-slick para el carrusel
import NavBar from '../components/NavBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GoogleReviews from '../components/GoogleReviews';
import AboutUs from '../components/AboutUs';

// Importar imágenes para las tarjetas
import heritageAirportImg from '../assets/images/HeritageCarAirport.jpg';
import heritageMKTImg from '../assets/images/HeritageMKT.jpg';
import escaladeImg from '../assets/images/Escalade1.jpg';
import mkt1Img from '../assets/images/MKT1.jpg';

// Importar CSS de slick-carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// Componente para cada tarjeta de servicio
function ServiceCard({ image, alt, title, description, contactLink, contactLabel }) {
  const [hover, setHover] = useState(false);

  const cardStyle = {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: hover
      ? '0 12px 30px rgba(0,0,0,0.3)'
      : '0 8px 20px rgba(0,0,0,0.2)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    transform: hover ? 'scale(1.03)' : 'scale(1)',
    padding: '30px',
    margin: '10px',
    minHeight: '600px',
  };

  const imageStyle = {
    ...styles.cardImage,
    transform: hover ? 'translateY(-10px)' : 'translateY(0)',
    transition: 'transform 0.3s ease',
  };

  return (
    <div
      style={cardStyle}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <img style={imageStyle} src={image} alt={alt} />
      <div style={styles.cardContent}>
        <h3 style={styles.cardTitle}>{title}</h3>
        <p style={styles.cardText}>{description}</p>
        {/* Botón único de contacto elegante */}
        <div style={styles.singleButtonContainer}>
          <a style={styles.singleContactButton} href={contactLink}>
            {contactLabel}
          </a>
        </div>
      </div>
    </div>
  );
}

function HomeScreen() {
  const [isMobile, setIsMobile] = useState(false);
  const [hasFooterRendered, setHasFooterRendered] = useState(false);

  // Detectar dispositivos móviles
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(
        /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        )
      );
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Renderizar el Footer una sola vez
  useEffect(() => {
    if (!hasFooterRendered) {
      setHasFooterRendered(true);
    }
  }, [hasFooterRendered]);

  // Configuración del carrusel (react-slick)
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 200,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 1000,
    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: 2 },
      },
      {
        breakpoint: 600,
        settings: { slidesToShow: 1 },
      },
    ],
  };

  return (
    <div style={styles.pageContainer}>
      <NavBar />
      <Header />

      <main style={styles.contentContainer}>
        {/* Sección Hero */}
        {!isMobile && (
          <section style={styles.heroSection}>
            <h1 style={styles.heroTitle}>Heritage Car Airport</h1>
            <p style={styles.heroSubtitle}>
              Premium and Reliable Transportation Services for All Your Needs
            </p>
          </section>
        )}

        {/* Sección AboutUs (siempre visible) */}
        <section id="aboutSection" style={styles.aboutSection}>
          <AboutUs />
        </section>

        {/* Sección Heritage (siempre visible) */}
        <section id="heritageSection" style={styles.heritageSection}>
          <div style={styles.sectionWrapper}>
            <h3 style={styles.heritageTitle}>
              Heritage Car Airport - Luxury Transportation
            </h3>
            <div style={styles.heritageParagraph}>
              <p>
                Experience unparalleled luxury and comfort with Heritage Car
                Airport, your trusted transportation service for local and
                long-distance travel across Westchester County and beyond. We
                are committed to providing first-class service with a touch of
                elegance, ensuring every journey is seamless and memorable.
              </p>
              <h4 style={styles.subTitle}>Service Areas</h4>
              <ul style={styles.list}>
                <li>Northern and Southern Westchester</li>
                <li>Dutchess County</li>
                <li>Orange County</li>
                <li>Putnam County</li>
                <li>Rockland County</li>
                <li>Local and long-distance destinations</li>
                <li>New York City</li>
              </ul>
              <h4 style={styles.subTitle}>Premium Airport Transfers</h4>
              <p>
                We provide reliable and punctual transfers to major airports,
                including:
              </p>
              <ul style={styles.list}>
                <li>John F. Kennedy International Airport (JFK)</li>
                <li>Newark Liberty International Airport (EWR)</li>
                <li>LaGuardia Airport (LGA)</li>
                <li>Westchester County Airport (HPN)</li>
                <li>Stewart International Airport</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Sección de Carrusel de Tarjetas */}
        <section style={styles.cardsSection}>
          <h2 style={styles.cardsTitle}>Explore Our Services</h2>
          <p style={styles.cardsSubtitle}>
            Choose from a variety of premium services tailored to your needs
          </p>
          <Slider {...sliderSettings}>
            <ServiceCard
              image={heritageAirportImg}
              alt="Heritage Car Airport"
              title="Heritage Car Airport"
              description="Arrive in timeless elegance with our Heritage Car Airport service—classic charm and modern luxury combined."
              contactLink="tel:8452258191"
              contactLabel="Call Now"
            />
            <ServiceCard
              image={heritageMKTImg}
              alt="Lincoln MKT"
              title="Lincoln MKT"
              description="Experience superior comfort and style in our Lincoln MKT—ideal for business trips, special events, and leisurely journeys."
              contactLink="sms:8452258191?body=Hello"
              contactLabel="Text Message"
            />
            <ServiceCard
              image={escaladeImg}
              alt="Out of Town"
              title="Out of Town"
              description="Embark on safe, reliable long-distance rides—perfect for spontaneous getaways and extended travel."
              contactLink="mailto:GOHERITAGELIMO@gmail.com"
              contactLabel="Email Us"
            />
            <ServiceCard
              image={mkt1Img}
              alt="Corporate Travel"
              title="Corporate Travel"
              description="Discover efficient transportation solutions designed for busy executives on the move."
              contactLink="https://wa.me/18452258191"
              contactLabel="WhatsApp Chat"
            />
          </Slider>
        </section>

        <GoogleReviews />
      </main>

      {hasFooterRendered && <Footer />}
    </div>
  );
}

// Estilos en objeto JS
const styles = {
  pageContainer: {
    backgroundColor: '#f8fafc',
    color: '#1f2937',
    margin: 0,
    padding: 0,
    overflowX: 'hidden',
    fontFamily: 'Segoe UI, Tahoma, sans-serif',
  },
  contentContainer: {
    width: '100%',
    margin: '0 auto',
    padding: 0,
  },
  heroSection: {
    width: '100%',
    minHeight: '60vh',
    background: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '60px 20px',
    color: '#000',
  },
  heroTitle: {
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '15px',
  },
  heroSubtitle: {
    fontSize: '20px',
    maxWidth: '600px',
    lineHeight: '1.5',
  },
  aboutSection: {
    padding: '50px 20px',
    backgroundColor: '#f3f4f6',
  },
  heritageSection: {
    backgroundColor: '#f9fafb',
    padding: '40px 20px',
    textAlign: 'center',
    // Sección siempre visible sin animaciones
    opacity: 1,
    transform: 'translateY(0)',
  },
  sectionWrapper: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  heritageTitle: {
    fontSize: '2.25rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: '#374151',
    marginBottom: '1rem',
  },
  heritageParagraph: {
    fontSize: '1rem',
    lineHeight: '1.6',
    color: '#374151',
    maxWidth: '800px',
    margin: '0 auto',
    textAlign: 'left',
  },
  subTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    marginTop: '1.5rem',
    marginBottom: '0.5rem',
    color: '#374151',
  },
  list: {
    listStyleType: 'disc',
    paddingLeft: '1.5rem',
    marginBottom: '1rem',
  },
  cardsSection: {
    padding: '60px 20px',
    backgroundColor: '#fff',
    textAlign: 'center',
  },
  cardsTitle: {
    fontSize: '28px',
    fontWeight: '600',
    marginBottom: '10px',
  },
  cardsSubtitle: {
    fontSize: '17px',
    color: '#6b7280',
    marginBottom: '30px',
  },
  cardImage: {
    width: '100%',
    height: '350px',
    objectFit: 'cover',
    borderRadius: '12px 12px 0 0',
  },
  cardContent: {
    padding: '20px',
    textAlign: 'left',
  },
  cardTitle: {
    fontSize: '22px',
    marginBottom: '10px',
    color: '#374151',
  },
  cardText: {
    fontSize: '16px',
    color: '#4b5563',
  },
  singleButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
  },
  singleContactButton: {
    background: 'linear-gradient(45deg, #3b82f6, #60a5fa)',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontSize: '16px',
    transition: 'background 0.3s ease, transform 0.3s ease',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
  },
};

export default HomeScreen;
